<template>
  <div>
    <!-- Header Section with Create User Button -->
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom border-2 border-secondary-subtle">
      <h2 class="mb-0 main-title">Assets</h2>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-md btn-primary" @click="openCreateUserModal">
          <i class="bi bi-person-plus-fill me-1"></i> Create User
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div id="filtersBar" class="filters-bar mb-3"></div>

    <!-- Reset Filters Button -->
    <div class="mb-3">
      <button @click="resetAllFilters" class="btn btn-sm btn-outline-secondary">
        <i class="fas fa-undo-alt me-1"></i> Reset Filters
      </button>
    </div>

    <!-- Table -->
    <div class="card p-1 bg-white">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th @click="sortBy('name')" class="sortable">
                Name <i :class="getSortIcon('name')"></i>
              </th>
              <th @click="sortBy('id')" class="sortable">
                ID <i :class="getSortIcon('id')"></i>
              </th>
              <th @click="sortBy('status')" class="sortable">
                Status <i :class="getSortIcon('status')"></i>
              </th>
              <th @click="sortBy('criticality')" class="sortable">
                Criticality <i :class="getSortIcon('criticality')"></i>
              </th>
              <th @click="sortBy('downtime')" class="sortable">
                Downtime <i :class="getSortIcon('downtime')"></i>
              </th>
              <th @click="sortBy('type')" class="sortable">
                Type <i :class="getSortIcon('type')"></i>
              </th>
              <th>Materials</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedData" :key="item.id">
              <td>{{ item.name }}</td>
              <td>{{ item.id }}</td>
              <td>
                <span :class="['status-badge', `status-${item.status.toLowerCase()}`]">
                  {{ item.status }}
                </span>
              </td>
              <td>{{ item.criticality }}</td>
              <td>{{ item.downtime || "-" }}</td>
              <td>{{ item.type || "-" }}</td>
              <td>
                <button v-if="item.materials && item.materials !== '-'" class="action-btn">
                  {{ item.materials.split(",")[0] }}
                </button>
                <span v-else>-</span>
              </td>
            </tr>
            <tr v-if="paginatedData.length === 0">
              <td colspan="7" class="text-center">No data found</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-wrapper" v-if="totalPages > 1">
        <div class="pagination-info">
          <!-- Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ sortedData.length }} entries -->
        </div>
        <div class="pagination-controls">
          <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
            <i class="fas fa-chevron-left"></i>
          </button>

          <div class="page-numbers">
            <button v-for="page in displayedPages" :key="page" @click="goToPage(page)"
              :class="['page-btn', { active: currentPage === page }]">
              {{ page }}
            </button>
          </div>

          <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- Page Size Selector -->
      <div class="page-size-wrapper">
        <label class="page-size-label">Rows per page:</label>
        <select v-model="pageSize" @change="resetPagination" class="page-size-select">
          <option :value="5">5</option>
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
      </div>
    </div>

    <!-- Create User Modal -->
    <div class="modal fade" id="createUserModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="createUserModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content modal-thin-header">
          <div class="modal-header">
            <h5 class="modal-title" id="createUserModalLabel">
              <i class="bi bi-person-plus-fill me-1"></i> Create New User
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          
          <div class="modal-body">
            <form @submit.prevent="submitUserForm">
              <!-- First Name -->
              <div class="mb-3">
                <label class="form-label form-label-thin">First Name <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-user"></i></span>
                  <input 
                    type="text" 
                    class="form-control form-control-thin"
                    name="first_name"
                    v-model="userFormData.first_name" 
                    placeholder="First Name" 
                    required
                  />
                </div>
                <div class="text-hint">Enter the user's first name</div>
              </div>

              <!-- Last Name -->
              <div class="mb-3">
                <label class="form-label form-label-thin">Last Name <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-user"></i></span>
                  <input 
                    type="text" 
                    class="form-control form-control-thin"
                    name="last_name"
                    v-model="userFormData.last_name" 
                    placeholder="Last Name" 
                    required
                  />
                </div>
                <div class="text-hint">Enter the user's last name</div>
              </div>

              <!-- Email Address -->
              <div class="mb-3">
                <label class="form-label form-label-thin">Email Address <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-envelope"></i></span>
                  <input 
                    type="email" 
                    class="form-control form-control-thin"
                    name="email"
                    v-model="userFormData.email" 
                    placeholder="Email Address" 
                    required
                  />
                </div>
                <div class="text-hint">Enter a valid email address</div>
              </div>

              <!-- External ID -->
              <div class="mb-3">
                <label class="form-label form-label-thin">External ID <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-id-card"></i></span>
                  <input 
                    type="text" 
                    class="form-control form-control-thin"
                    name="id"
                    v-model="userFormData.id" 
                    placeholder="External ID" 
                    required
                  />
                </div>
                <div class="text-hint">Unique external identifier for the user</div>
              </div>

              <!-- Password -->
              <div class="mb-3">
                <label class="form-label form-label-thin">Password <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-lock"></i></span>
                  <input 
                    type="password" 
                    class="form-control form-control-thin"
                    name="password"
                    v-model="userFormData.password" 
                    placeholder="Password" 
                    required
                  />
                </div>
                <div class="text-hint">Minimum 8 characters with at least one number and special character</div>
              </div>

              <!-- Phone (Optional) -->
              <div class="mb-3">
                <label class="form-label form-label-thin">Phone (Optional)</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-phone"></i></span>
                  <input 
                    type="text" 
                    class="form-control form-control-thin"
                    name="phone"
                    v-model="userFormData.phone" 
                    placeholder="Phone (Optional)"
                  />
                </div>
                <div class="text-hint">Contact number (optional)</div>
              </div>

              <!-- Role Selection -->
              <div class="mb-3">
                <label class="form-label form-label-thin">User Role</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-user-tag"></i></span>
                  <select class="form-select form-select-thin" v-model="userFormData.role">
                    <option value="user">User</option>
                    <option value="admin">Administrator</option>
                    <option value="manager">Manager</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                <div class="text-hint">Select user permission level</div>
              </div>

              <!-- Department -->
              <div class="mb-3">
                <label class="form-label form-label-thin">Department</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="fas fa-building"></i></span>
                  <input 
                    type="text" 
                    class="form-control form-control-thin"
                    v-model="userFormData.department" 
                    placeholder="e.g., IT, HR, Operations"
                  />
                </div>
                <div class="text-hint">Department name (optional)</div>
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">
              <i class="bi bi-x-circle"></i> Cancel
            </button>
            <button type="button" class="btn btn-primary text-light hover-white btn-md" @click="submitUserForm">
              <i class="bi bi-person-plus"></i> Create User
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ViewAssets',
  data() {
    return {
      filterManager: null,
      // Sorting
      sortColumn: 'name',
      sortOrder: 'asc',
      // Pagination
      currentPage: 1,
      pageSize: 10,
      // Data
      allData: [
        { id: "#16923684", name: "testing", status: "Online", criticality: "Critical", downtime: "planned", type: "machine", parts: "handle" },
        { id: "#16919732", name: "My asset", status: "Online", criticality: "High", downtime: "planned", type: "machine", parts: "-" },
        { id: "#16521870", name: "Test", status: "Online", criticality: "Important", downtime: "unplanned", type: "machine", parts: "handle" },
        { id: "#16521867", name: "ABC", status: "Online", criticality: "Critical", downtime: "planned", type: "machine", parts: "handle, +1" },
        { id: "#16521855", name: "Production Line", status: "Offline", criticality: "High", downtime: "unplanned", type: "conveyor", parts: "-" },
        { id: "#16521844", name: "Robot Arm", status: "Online", criticality: "Critical", downtime: "planned", type: "robot", parts: "handle" },
        { id: "#16521833", name: "Conveyor Belt", status: "Offline", criticality: "Medium", downtime: "unplanned", type: "conveyor", parts: "-" },
        { id: "#16521822", name: "Generator", status: "Online", criticality: "Important", downtime: "planned", type: "generator", parts: "handle, +2" },
        { id: "#16521811", name: "HVAC System", status: "Warning", criticality: "High", downtime: "planned", type: "hvac", parts: "handle" },
        { id: "#16521800", name: "Pump Unit", status: "Online", criticality: "Critical", downtime: "unplanned", type: "pump", parts: "-" },
        { id: "#16521799", name: "Compressor", status: "Offline", criticality: "Important", downtime: "planned", type: "compressor", parts: "handle" },
        { id: "#16521788", name: "Boiler", status: "Online", criticality: "High", downtime: "unplanned", type: "boiler", parts: "-" }
      ],
      filteredData: [],
      userFormData: {
        first_name: '',
        last_name: '',
        email: '',
        id: '',
        password: '',
        phone: '',
        role: 'user',
        department: ''
      },
      userModalInstance: null
    };
  },
  computed: {
    // Sorted data
    sortedData() {
      let result = [...this.filteredData];

      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];

          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';

          if (this.sortColumn === 'id') {
            valA = parseInt(String(valA).replace('#', '')) || 0;
            valB = parseInt(String(valB).replace('#', '')) || 0;
          }

          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();

          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    },

    // Total pages
    totalPages() {
      return Math.ceil(this.sortedData.length / this.pageSize);
    },

    // Current page data
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.sortedData.slice(start, end);
    },

    // Start index for display
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },

    // End index for display
    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.sortedData.length);
    },

    // Displayed page numbers (with ellipsis)
    displayedPages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const pages = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 3) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    }
  },
  mounted() {
    this.filteredData = [...this.allData];

    this.$nextTick(() => {
      setTimeout(() => {
        this.initFilters();
      }, 100);
    });

    // Initialize User Modal
    const userModalEl = document.getElementById('createUserModal');
    if (userModalEl) {
      this.userModalInstance = new bootstrap.Modal(userModalEl, {
        backdrop: 'static',
        keyboard: false
      });
    }
  },
  methods: {
    initFilters() {
      if (typeof $ === "undefined" || typeof GenericFilterManager === "undefined") return;

      this.filterManager = new GenericFilterManager({
        containerId: "filtersBar",
        filters: [
          {
            key: "id",
            label: "ID",
            type: "input",
            placeholder: "Filter by ID"
          },
          {
            key: "status",
            label: "Status",
            type: "checkbox",
            options: [
              { value: "online", label: "Online" },
              { value: "offline", label: "Offline" },
              { value: "warning", label: "Warning" }
            ]
          },
          {
            key: "criticality",
            label: "Criticality",
            type: "checkbox",
            options: [
              { value: "critical", label: "Critical" },
              { value: "important", label: "Important" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" }
            ]
          }
        ],
        onFilterChange: (filters) => {
          this.applyFilters(filters);
        }
      });
    },

    applyFilters(activeFilters) {
      let result = [...this.allData];

      // For input type (string value)
      if (activeFilters.id) {
        result = result.filter(item =>
          item.id.toLowerCase().includes(activeFilters.id.toLowerCase())
        );
      }

      // For checkbox type (array value)
      if (activeFilters.status?.length) {
        result = result.filter(item =>
          activeFilters.status.includes(item.status.toLowerCase())
        );
      }

      if (activeFilters.criticality?.length) {
        result = result.filter(item =>
          activeFilters.criticality.includes(item.criticality.toLowerCase())
        );
      }

      this.filteredData = result;
      this.resetPagination();
    },

    // Sorting method
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortOrder = 'asc';
      }
      this.resetPagination();
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'fas fa-sort text-muted';
      }
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
    },

    // Pagination methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      if (page !== '...') {
        this.currentPage = page;
      }
    },

    resetPagination() {
      this.currentPage = 1;
    },

    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters();
      }
      this.filteredData = [...this.allData];
      this.sortColumn = 'name';
      this.sortOrder = 'asc';
      this.resetPagination();
    },

    openCreateUserModal() {
      if (this.userModalInstance) {
        this.resetUserForm();
        this.userModalInstance.show();
      }
    },

    submitUserForm() {
      console.log('User Data:', this.userFormData);
      this.$emit('user-created', this.userFormData);
      this.resetUserForm();
      this.userModalInstance.hide();
    },

    resetUserForm() {
      this.userFormData = {
        first_name: '',
        last_name: '',
        email: '',
        id: '',
        password: '',
        phone: '',
        role: 'user',
        department: ''
      };
    }
  },
  beforeDestroy() {
    if (this.filterManager) {
      this.filterManager.destroy();
    }
    if (this.userModalInstance) {
      const modalEl = document.getElementById('createUserModal');
      if (modalEl) {
        modalEl.removeEventListener('hidden.bs.modal', null);
        modalEl.removeEventListener('shown.bs.modal', null);
      }
    }
  }
}
</script>

